import { useAppSelector } from '@lumora/common';
import React, { useState } from 'react';

import { AIChatbot } from './AIChatbot';

const styles = `
  /* Scoped chatbot styles - keep concise to avoid brittle large overrides */
  .lumora-chatbot-root { position: fixed; right: 20px; bottom: 20px; z-index: 60; }
  .lumora-chatbot-toggle { width: 56px; height: 56px; border-radius: 50%; background: #2563eb; color: white; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 18px rgba(15,23,42,0.18); cursor: pointer; }

  .lumora-chatbot-panel { width: 400px; max-width: calc(100vw - 40px); height: 600px; border-radius: 14px; overflow: hidden; box-shadow: 0 12px 40px rgba(2,6,23,0.3); background: white; display: flex; flex-direction: column; }
  @media (min-width: 640px) { .lumora-chatbot-panel { width: 480px; height: 640px; } }

  .lumora-chatbot-panel-expanded { width: 95vw !important; height: 95vh !important; max-width: none !important; position: fixed !important; top: 2.5vh !important; left: 2.5vw !important; z-index: 100 !important; }

  .lumora-chatbot-header { padding: 12px 16px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #eef2ff; background: linear-gradient(90deg, rgba(59,130,246,0.04), rgba(99,102,241,0.02)); }
  .lumora-chatbot-title { font-weight: 600; color: #0f172a; font-size: 14px; }

  /* Message container scrolling */
  .lumora-chatbot-message-container {
    scroll-behavior: smooth;
    padding-bottom: 20px; /* Extra space for sticky input */
    overflow-y: auto;
    max-height: 100%;
  }
  .lumora-chatbot-message-container::-webkit-scrollbar {
    width: 6px;
  }
  .lumora-chatbot-message-container::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .lumora-chatbot-message-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .lumora-chatbot-message-container::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

export const GlobalChatbot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Check if user is authenticated
  const { isAuthenticated } = useAppSelector(state => state.auth);

  // Don't render anything if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // The AI chatbot handles its own data fetching, so we don't need Redux selectors here

  return (
    <div className="lumora-chatbot-root" aria-hidden={false}>
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      {!open ? (
        <div
          className="lumora-chatbot-toggle"
          onClick={() => setOpen(true)}
          title="Open chat"
        >
          {/* simple chat icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="currentColor"
            aria-hidden
          >
            <path d="M12 3C7.03 3 3 6.58 3 11c0 1.86.72 3.56 1.93 4.9L4 21l5.4-1.43C10.07 19.77 11 20 12 20c4.97 0 9-3.58 9-8s-4.03-9-9-9z" />
          </svg>
        </div>
      ) : (
        <div
          className={`lumora-chatbot-panel ${expanded ? 'lumora-chatbot-panel-expanded' : ''}`}
          role="dialog"
          aria-label="Portfolio chat"
        >
          <div className="lumora-chatbot-header">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="#2563eb"
                aria-hidden
              >
                <path d="M12 3C7.03 3 3 6.58 3 11c0 1.86.72 3.56 1.93 4.9L4 21l5.4-1.43C10.07 19.77 11 20 12 20c4.97 0 9-3.58 9-8s-4.03-9-9-9z" />
              </svg>
            </div>
            <div className="lumora-chatbot-title">Portfolio assistant</div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Collapse chat' : 'Expand chat'}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="#6b7280"
                  strokeWidth="2"
                  aria-hidden
                >
                  {expanded ? (
                    // Minimize icon
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 0 2-2h3M3 16h3a2 2 0 0 0 2 2v3" />
                  ) : (
                    // Maximize icon
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                  )}
                </svg>
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="#6b7280"
                  aria-hidden
                >
                  <path d="M6.225 4.811L4.811 6.225 10.586 12l-5.775 5.775 1.414 1.414L12 13.414l5.775 5.775 1.414-1.414L13.414 12l5.775-5.775-1.414-1.414L12 10.586z" />
                </svg>
              </button>
            </div>
          </div>

          <div
            style={{
              height: 'calc(100% - 60px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <AIChatbot />
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalChatbot;
